import { DocumentNode } from "graphql";
import gql from "graphql-tag";

import { BaseApi } from "../base";
import { TOrganizationSettingsMain } from "../organization-settings/types";
import { IOrganizationAdminSettings, OrganizationPlanEnum, TOrganization, TSearchOrganizations } from "./types";

export class OrganizationApi extends BaseApi {
  async create(variables: {
    aiLicensesProvisioned: number;
    companyName: string;
    firstUserEmail: string;
    firstUserName: string;
  }): Promise<number> {
    type TVariables = {
      aiLicensesProvisioned: number;
      companyName: string;
      firstUserEmail: string;
      firstUserName: string;
    };
    type TData = { support: { organization: { create: { id: number } } } };
    const query: DocumentNode = gql`
      mutation CreateOrganization(
        $companyName: String!
        $firstUserEmail: String!
        $firstUserName: String!
        $aiLicensesProvisioned: Int = 0
      ) {
        support {
          organization {
            create(
              companyName: $companyName
              firstUserEmail: $firstUserEmail
              firstUserName: $firstUserName
              aiLicensesProvisioned: $aiLicensesProvisioned
            ) {
              id
            }
          }
        }
      }
    `;

    try {
      const result: TData = await this.mutate<TData, TVariables>(query, variables);
      this.postSuccess("Successfully created organization");
      return result.support.organization.create.id;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getById(id: number): Promise<Omit<TOrganization, "settings">> {
    type TVariables = { id: number };
    type TData = {
      organization_by_pk: TOrganization;
    };

    const query: DocumentNode = gql`
      query GetOrganizationByPK($id: Int!) {
        organization_by_pk(id: $id) {
          name
          plan
          shouldDeleteAt
          enabled
        }
      }
    `;

    try {
      const result: TData = await this.query<TData, TVariables>(query, { id });
      return result.organization_by_pk;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateName(name: string, id: number): Promise<TOrganization> {
    type TVariables = { id: number; name: string };
    type TData = { update_organization_by_pk: TOrganization };
    const query: DocumentNode = gql`
      mutation UpdateOrganizationName($id: Int!, $name: String = "") {
        update_organization_by_pk(pk_columns: { id: $id }, _set: { name: $name }) {
          enabled
          name
          shouldDeleteAt
          plan
        }
      }
    `;

    try {
      const result: TData = await this.mutate<TData, TVariables>(query, { id, name });
      this.postSuccess("Successfully updated organization name");
      return result.update_organization_by_pk;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updatePlan(plan: OrganizationPlanEnum, id: number): Promise<TOrganization> {
    type TVariables = { id: number; plan: OrganizationPlanEnum };
    type TData = { update_organization_by_pk: TOrganization };

    const query: DocumentNode = gql`
      mutation UpdateOrganizationPlan($id: Int!, $plan: String = "") {
        update_organization_by_pk(pk_columns: { id: $id }, _set: { plan: $plan }) {
          plan
        }
      }
    `;

    try {
      const result: TData = await this.mutate<TData, TVariables>(query, { id, plan });
      this.postSuccess("Successfully updated organization plan");
      return result.update_organization_by_pk;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateEnabled(enabled: boolean, id: number): Promise<TOrganization> {
    type TVariables = { enabled: boolean; id: number };
    type TData = { update_organization_by_pk: TOrganization };

    const query: DocumentNode = gql`
      mutation UpdateOrganizationEnabled($id: Int!, $enabled: Boolean = false) {
        update_organization_by_pk(pk_columns: { id: $id }, _set: { enabled: $enabled }) {
          enabled
        }
      }
    `;

    try {
      const result: TData = await this.mutate<TData, TVariables>(query, { enabled, id });
      this.postSuccess("Successfully updated organization enabled");
      return result.update_organization_by_pk;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateShouldDeleteAt(shouldDeleteAt: string, id: number): Promise<TOrganization> {
    type TVariables = { id: number; shouldDeleteAt: string };
    type TData = { update_organization_by_pk: TOrganization };

    const query: DocumentNode = gql`
      mutation UpdateOrganizationShouldDelete($id: Int!, $shouldDeleteAt: timestamptz) {
        update_organization_by_pk(pk_columns: { id: $id }, _set: { shouldDeleteAt: $shouldDeleteAt }) {
          shouldDeleteAt
        }
      }
    `;

    try {
      const result: TData = await this.mutate<TData, TVariables>(query, { id, shouldDeleteAt });
      this.postSuccess("Successfully updated organization should delete at");
      return result.update_organization_by_pk;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateAdminSettings(
    settings: Partial<IOrganizationAdminSettings>,
    options?: { hideSuccessMessage: boolean }
  ): Promise<IOrganizationAdminSettings> {
    type TVariables = { updatedSettings: Partial<IOrganizationAdminSettings> };
    type TData = { momentum: { organization: { updateAdminSettings: { adminSettings: IOrganizationAdminSettings } } } };

    const query: DocumentNode = gql`
      mutation UpdateOrganizationAdminSettings($updatedSettings: TGqlUpdateOrganizationAdminSettingsInput!) {
        momentum {
          organization {
            updateAdminSettings(input: $updatedSettings) {
              adminSettings
            }
          }
        }
      }
    `;

    try {
      const result: TData = await this.mutate<TData, TVariables>(query, {
        updatedSettings: settings,
      });
      if (!options?.hideSuccessMessage) {
        this.postSuccess("Successfully updated organization authentication settings");
      }
      return result?.momentum?.organization?.updateAdminSettings?.adminSettings;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateAdminSettingsForSupport(
    organizationId: number,
    settings: Partial<IOrganizationAdminSettings>
  ): Promise<IOrganizationAdminSettings> {
    type TVariables = { organizationId: number; updatedSettings: Partial<IOrganizationAdminSettings> };
    type TData = {
      support: { organization: { updateAdminSettings: { adminSettings: IOrganizationAdminSettings } } };
    };

    const query: DocumentNode = gql`
      mutation UpdateOrganizationAdminSettingsForSupport(
        $organizationId: Float!
        $updatedSettings: TGqlUpdateOrganizationAdminSettingsInput!
      ) {
        support {
          organization {
            updateAdminSettings(input: $updatedSettings, organizationId: $organizationId) {
              adminSettings
            }
          }
        }
      }
    `;

    try {
      const result: TData = await this.mutate<TData, TVariables>(query, {
        organizationId,
        updatedSettings: settings,
      });
      this.postSuccess("Successfully updated organization authentication settings");
      return result.support.organization.updateAdminSettings.adminSettings;
    } catch (error) {
      this.handleError(error);
    }
  }

  async get(): Promise<TOrganization> {
    type TData = {
      momentum: { organization: { settings: { settings: TOrganizationSettingsMain } } };
      organization: TOrganization;
    };
    const query: DocumentNode = gql`
      query OrganizationSettings {
        organization {
          id
          name
        }
        momentum {
          organization {
            settings {
              settings
            }
          }
        }
      }
    `;

    try {
      const result: TData = await this.query<TData, null>(query);
      return {
        ...result.organization[0],
        settings: {
          settings: result.momentum.organization.settings.settings,
        },
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  async getAdminSettings(): Promise<IOrganizationAdminSettings> {
    type TData = { momentum: { organization: { settings: { adminSettings: IOrganizationAdminSettings } } } };
    const query: DocumentNode = gql`
      query GetOrganizationAdminSettings {
        momentum {
          organization {
            settings {
              adminSettings
            }
          }
        }
      }
    `;

    try {
      const result: TData = await this.query<TData, null>(query);
      return result?.momentum?.organization?.settings?.adminSettings;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getAdminSettingsForSupport(organizationId: number): Promise<IOrganizationAdminSettings> {
    type TData = {
      support: { organization: { settings: { adminSettings: IOrganizationAdminSettings } } };
    };
    const query: DocumentNode = gql`
      query GetOrganizationAdminSettingsForSupport($organizationId: Float!) {
        support {
          organization {
            settings {
              adminSettings(organizationId: $organizationId)
            }
          }
        }
      }
    `;

    try {
      const result: TData = await this.query<TData, { organizationId: number }>(query, { organizationId });
      return result.support.organization.settings.adminSettings;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getAiLicenseConstraints(organizationId: number): Promise<TOrganization> {
    type TVariables = { organizationId: number };
    type TData = { organization: TOrganization };
    const query: DocumentNode = gql`
      query GetOrganizationAiLicenseConstraints($organizationId: Int!) {
        organization(where: { id: { _eq: $organizationId } }) {
          id
          userLicenseConstraints
        }
      }
    `;

    try {
      const result: TData = await this.query<TData, TVariables>(query, { organizationId });
      return result.organization[0];
    } catch (error) {
      this.handleError(error);
    }
  }

  async searchOrganizations(where: object): Promise<TSearchOrganizations[]> {
    type TVariables = {
      where:
        | {
            deleted_at: { _is_null: boolean };
            enabled: { _eq: boolean };
          }
        | {};
    };
    type TData = {
      organization: TSearchOrganizations[];
    };

    const query: DocumentNode = gql`
      query SearchOrganizations($where: organization_bool_exp!) {
        organization(where: $where, order_by: { name: asc }) {
          id
          deleted_at
          enabled
          name
          integrations {
            service
            endpoint
          }
          salesforce_organizations {
            city
            country
            instanceName
            isSandbox
            name
            salesforceOrganizationId
            salesforceOrganizationType
          }
          shouldDeleteAt
        }
      }
    `;

    try {
      const response = await this.query<TData, TVariables>(query, { where });
      return response.organization;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getOrganizationById(organizationId: number): Promise<TOrganization> {
    type TVariables = { organizationId: number };
    type TData = { organization: TOrganization[] };

    const query: DocumentNode = gql`
      query GetOrganization($organizationId: Int!) {
        organization(where: { id: { _eq: $organizationId } }) {
          deleted_at
          id
          name
          shouldDeleteAt
        }
      }
    `;

    try {
      const response = await this.query<TData, TVariables>(query, { organizationId });
      return response.organization[0];
    } catch (error) {
      this.handleError(error);
    }
  }

  async getIsSalesforceSandbox(): Promise<boolean> {
    type TData = { momentum: { organization: { isSalesforceSandbox: boolean } } };
    const query: DocumentNode = gql`
      query GetOrganizationAdminSettings {
        momentum {
          organization {
            isSalesforceSandbox
          }
        }
      }
    `;

    try {
      const result: TData = await this.query<TData, null>(query);
      return result?.momentum?.organization?.isSalesforceSandbox;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getAuthUrl(): Promise<string> {
    type TData = { momentum: { organization: { authUrl: string } } };
    const query: DocumentNode = gql`
      query GetOrganizationAuthUrl {
        momentum {
          organization {
            authUrl
          }
        }
      }
    `;

    try {
      const result: TData = await this.query<TData, null>(query);
      return result?.momentum?.organization?.authUrl;
    } catch (error) {
      this.handleError(error);
    }
  }
}
